const MS_DIA = 24 * 60 * 60 * 1000;

export const HORA_INICIO_AGENDA = 8;
export const HORA_FIN_AGENDA = 20;
export const ALTO_HORA_AGENDA = 68;

const pad2 = (value) => String(value).padStart(2, '0');

export const crearFechaLocal = (year, monthIndex, day = 1, hours = 0, minutes = 0) =>
    new Date(year, monthIndex, day, hours, minutes, 0, 0);

export const formatearIdDia = (date) =>
    `${pad2(date.getDate())}-${pad2(date.getMonth() + 1)}-${date.getFullYear()}`;

export const formatearIsoDia = (date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

export const parsearIdDia = (idDia) => {
    if (!idDia) return new Date();

    const [day, month, year] = String(idDia).split('-').map(Number);
    return crearFechaLocal(year, month - 1, day);
};

export const parsearFechaSql = (value) => {
    if (!value) return null;

    const raw = String(value).trim();
    const [fechaRaw, horaRaw = '09:00:00'] = raw.replace('T', ' ').split(' ');
    const [year, month, day] = fechaRaw.split('-').map(Number);

    if (!year || !month || !day) return null;

    const [hour = 9, minute = 0] = horaRaw.split(':').map(Number);
    return crearFechaLocal(year, month - 1, day, hour || 0, minute || 0);
};

export const construirDiasMes = (mesActual) => {
    const primerDiaMes = crearFechaLocal(mesActual.getFullYear(), mesActual.getMonth(), 1);
    const diaSemana = primerDiaMes.getDay() === 0 ? 6 : primerDiaMes.getDay() - 1;
    const primerDiaGrid = new Date(primerDiaMes.getTime() - diaSemana * MS_DIA);

    return Array.from({ length: 42 }, (_, index) =>
        new Date(primerDiaGrid.getTime() + index * MS_DIA)
    );
};

export const generarHorasAgenda = () =>
    Array.from({ length: HORA_FIN_AGENDA - HORA_INICIO_AGENDA + 1 }, (_, index) =>
        `${pad2(HORA_INICIO_AGENDA + index)}:00`
    );

export const etiquetaMesCalendario = (date) =>
    date.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

export const etiquetaDiaCompleto = (idDia) =>
    parsearIdDia(idDia).toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });

export const formatearMinutos = (minutos) => {
    const total = Number(minutos || 0);
    const horas = Math.floor(total / 60);
    const resto = total % 60;

    if (!total) return '';
    if (!horas) return `${resto} min`;
    if (!resto) return `${horas} h`;
    return `${horas} h ${resto} min`;
};

export const normalizarTareasCalendario = (tareas = []) =>
    tareas
        .map((tarea) => {
            const inicio = parsearFechaSql(tarea.fecha_calen || tarea.fecha_calendario || tarea.fecha);

            if (!inicio) return null;

            const minutosEstimados = Number(tarea.minutos_estimados || tarea.tiempo_estimado || 60) || 60;
            const fin = new Date(inicio.getTime() + minutosEstimados * 60 * 1000);
            const fecha = formatearIdDia(inicio);
            const horaInicio = `${pad2(inicio.getHours())}:${pad2(inicio.getMinutes())}`;
            const horaFin = `${pad2(fin.getHours())}:${pad2(fin.getMinutes())}`;

            return {
                ...tarea,
                id: tarea.id,
                fecha,
                fecha_iso: formatearIsoDia(inicio),
                hora: horaInicio,
                hora_inicio: horaInicio,
                hora_final: horaFin,
                inicio,
                fin,
                minutos_estimados: minutosEstimados,
                titulo: tarea.asunto || `Tarea #${tarea.id}`,
                proyecto: tarea.proyecto || tarea.nombre_proyecto || tarea.nombreProyecto || '',
                cliente: tarea.cliente || tarea.nombre_cliente || '',
                estado: tarea.estado || tarea.nombre_estado || '',
                color_estado: tarea.estado_color || tarea.color_estado || tarea.color || '#e53935',
                tipo_calendario: 'tarea',
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.inicio - b.inicio);

export const normalizarCitasCalendario = (citas = []) =>
    citas
        .map((cita) => {
            const inicio = parsearFechaSql(cita.fecha);

            if (!inicio) return null;

            const descripcionCita = String(cita.descripcion || '');
            const lineaClienteCita = descripcionCita
                .split(/\r?\n/)
                .find(linea => linea.startsWith('CLIENTE_TAREA:'));
            const clienteCita = lineaClienteCita
                ? lineaClienteCita.replace('CLIENTE_TAREA:', '').trim()
                : '';
            const lineaTrabajadoresCita = descripcionCita
                .split(/\r?\n/)
                .find(linea => linea.startsWith('TRABAJADORES_TAREA:'));
            const trabajadoresCita = lineaTrabajadoresCita
                ? lineaTrabajadoresCita.replace('TRABAJADORES_TAREA:', '').trim()
                : '';
            const matchIdTarea = descripcionCita.match(/ID_TAREA_CALENDARIO:(\d+);/);
            const idTareaOrigen = matchIdTarea ? Number(matchIdTarea[1]) : null;
            const fin = parsearFechaSql(cita.fecha_fin) || new Date(inicio.getTime() + 60 * 60 * 1000);
            const fecha = formatearIdDia(inicio);
            const horaInicio = cita.hora_inicio ? String(cita.hora_inicio).slice(0, 5) : '';
            const horaFin = cita.hora_final ? String(cita.hora_final).slice(0, 5) : '';

            return {
                ...cita,
                id: `cita-${cita.id}`,
                id_cita: cita.id,
                fecha,
                fecha_iso: formatearIsoDia(inicio),
                hora: horaInicio,
                hora_inicio: horaInicio,
                hora_final: horaFin,
                inicio,
                fin,
                minutos_estimados: Math.max((fin - inicio) / 60000, 60),
                titulo: cita.asunto || `Cita #${cita.id}`,
                proyecto: '',
                cliente: cita.cliente || cita.nombre_cliente || clienteCita,
                trabajadores_asignados: trabajadoresCita,
                id_tarea_origen: idTareaOrigen,
                agendada_desde_tarea: Boolean(idTareaOrigen),
                finalizado: Number(cita.finalizado || 0),
                estado: Number(cita.finalizado || 0) === 1 ? 'Finalizado' : 'Recordatorio',
                color_estado: Number(cita.finalizado || 0) === 1 ? '#6fbf8f' : '#e53935',
                tipo_calendario: 'cita',
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.inicio - b.inicio);

export const ordenarEventosCalendario = (a, b) => {
    const finalizadoA = Number(a.finalizado || 0) === 1 ? 1 : 0;
    const finalizadoB = Number(b.finalizado || 0) === 1 ? 1 : 0;

    if (finalizadoA !== finalizadoB) {
        return finalizadoA - finalizadoB;
    }

    return a.inicio - b.inicio;
};

export const obtenerEventosDia = (eventos, idDia) =>
    eventos
        .filter((evento) => evento.fecha === idDia)
        .sort(ordenarEventosCalendario);

export const cambiarDiaEvento = (evento, idDia) => {
    const nuevoDia = parsearIdDia(idDia);
    const inicio = crearFechaLocal(
        nuevoDia.getFullYear(),
        nuevoDia.getMonth(),
        nuevoDia.getDate(),
        evento.inicio.getHours(),
        evento.inicio.getMinutes()
    );
    const duracion = Math.max((evento.fin - evento.inicio) / 60000, 15);
    const fin = new Date(inicio.getTime() + duracion * 60 * 1000);

    return {
        ...evento,
        fecha: formatearIdDia(inicio),
        fecha_iso: formatearIsoDia(inicio),
        inicio,
        fin,
    };
};

export const calcularPosicionEvento = (evento) => {
    const inicioAgenda = crearFechaLocal(
        evento.inicio.getFullYear(),
        evento.inicio.getMonth(),
        evento.inicio.getDate(),
        HORA_INICIO_AGENDA
    );
    const finAgenda = crearFechaLocal(
        evento.inicio.getFullYear(),
        evento.inicio.getMonth(),
        evento.inicio.getDate(),
        HORA_FIN_AGENDA
    );
    const inicio = evento.inicio < inicioAgenda ? inicioAgenda : evento.inicio;
    const fin = evento.fin > finAgenda ? finAgenda : evento.fin;
    const minutosInicio = Math.max((inicio - inicioAgenda) / 60000, 0);
    const duracionMinutos = Math.max((fin - inicio) / 60000, 15);

    return {
        top: (minutosInicio / 60) * ALTO_HORA_AGENDA + 4,
        height: Math.max((duracionMinutos / 60) * ALTO_HORA_AGENDA - 8, 44),
    };
};
